import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useApp } from "../AppContext";

/**
 * Global prefetched cache for the most-used lists.
 * - Loads in parallel as soon as a user is authenticated.
 * - Exposes both data + `refresh(scope)` to invalidate a specific slice.
 * - All consumers read instantly from the cache (no spinner on subsequent navigation).
 */
const Ctx = createContext({ clients: [], projects: [], quotes: [], invoices: [], ready: false, refresh: () => {} });

export function PrefetchProvider({ children }) {
  const { token } = useApp();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async (scope) => {
    if (!token) return;
    const jobs = [];
    if (!scope || scope === "clients") jobs.push(axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => {}));
    if (!scope || scope === "projects") jobs.push(axios.get(`${API}/projects`).then((r) => setProjects(r.data || [])).catch(() => {}));
    if (!scope || scope === "quotes") jobs.push(axios.get(`${API}/quotes`).then((r) => setQuotes(r.data || [])).catch(() => {}));
    if (!scope || scope === "invoices") jobs.push(axios.get(`${API}/invoices`).then((r) => setInvoices(r.data || [])).catch(() => {}));
    await Promise.all(jobs);
    setReady(true);
  }, [token]);

  useEffect(() => { if (token) refresh(); else { setClients([]); setProjects([]); setQuotes([]); setInvoices([]); setReady(false); } }, [token, refresh]);

  return <Ctx.Provider value={{ clients, projects, quotes, invoices, ready, refresh }}>{children}</Ctx.Provider>;
}

export const usePrefetch = () => useContext(Ctx);
